import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Menu, X, LogOut, Car, History, LayoutDashboard, Wrench, Truck, Tag, Gauge, MessageCircle, Mail, Trash2 } from "lucide-react";
import NotificationBell from "./NotificationBell";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../api";
import { toast } from "sonner";

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/account");
      toast.success("Hesabınız başarıyla silindi");
      setShowDelete(false);
      await logout();
      navigate("/");
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setDeleting(false);
  };

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  const goContact = () => {
    setOpen(false);
    const scroll = () => document.getElementById("iletisim")?.scrollIntoView({ behavior: "smooth" });
    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(scroll, 500);
    } else {
      scroll();
    }
  };

  const links = (
    <>
      <button data-testid="nav-iletisim" onClick={goContact}
        className="flex items-center gap-2 text-sm nav-ink transition-colors">
        <Mail className="w-4 h-4" /> İletişim
      </button>
      {user && (
        <>
          <Link data-testid="nav-my-queries" to="/gecmis-sorgularim" onClick={() => setOpen(false)}
            className="flex items-center gap-2 text-sm nav-ink transition-colors">
            <History className="w-4 h-4" /> Geçmiş Sorgularım
          </Link>
          <Link data-testid="nav-my-vehicles" to="/araclarim" onClick={() => setOpen(false)}
            className="flex items-center gap-2 text-sm nav-ink transition-colors">
            <Car className="w-4 h-4" /> Araçlarım
          </Link>
          {user.role === "servis" && (
            <Link data-testid="nav-servis-panel" to="/servis-panel" onClick={() => setOpen(false)}
              className="flex items-center gap-2 text-sm nav-ink transition-colors">
              <Wrench className="w-4 h-4" /> İşletme Paneli
            </Link>
          )}
          {user.role === "cekici" && (
            <Link data-testid="nav-cekici-panel" to="/cekici-panel" onClick={() => setOpen(false)}
              className="flex items-center gap-2 text-sm nav-ink transition-colors">
              <Truck className="w-4 h-4" /> Çekici Paneli
            </Link>
          )}
          {user.role === "user" && (
            <>
              <Link data-testid="nav-hizmetler" to="/hizmetler" onClick={() => setOpen(false)}
                className="flex items-center gap-2 text-sm nav-ink transition-colors">
                <Truck className="w-4 h-4" /> Hizmetler
              </Link>
              <Link data-testid="nav-tekliflerim" to="/tekliflerim" onClick={() => setOpen(false)}
                className="flex items-center gap-2 text-sm nav-ink transition-colors">
                <Tag className="w-4 h-4" /> Tekliflerim
              </Link>
              <Link data-testid="nav-degerleme" to="/degerleme" onClick={() => setOpen(false)}
                className="flex items-center gap-2 text-sm nav-ink transition-colors">
                <Gauge className="w-4 h-4" /> Değerleme
              </Link>
            </>
          )}
          {user.role !== "admin" && (
            <Link data-testid="nav-geri-bildirim" to="/geri-bildirim" onClick={() => setOpen(false)}
              className="flex items-center gap-2 text-sm nav-ink transition-colors">
              <MessageCircle className="w-4 h-4" /> Geri Bildirim
            </Link>
          )}
        </>
      )}
      {user && user.role === "admin" && (
        <Link data-testid="nav-admin" to="/admin" onClick={() => setOpen(false)}
          className="flex items-center gap-2 text-sm text-orange-300 hover:text-orange-200 transition-colors">
          <LayoutDashboard className="w-4 h-4" /> Yönetim Paneli
        </Link>
      )}
      {user && (
        <button data-testid="nav-delete-account" onClick={() => { setOpen(false); setShowDelete(true); }}
          className="flex items-center gap-2 text-sm text-red-400/90 hover:text-red-300 transition-colors md:border-l md:border-white/10 md:pl-6">
          <Trash2 className="w-4 h-4" /> Hesabımı Sil
        </button>
      )}
    </>
  );

  return (
    <>
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0B1B33]/95 border-b border-orange-400/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link data-testid="nav-logo" to="/" className="flex items-center gap-3 group">
          <span className="w-11 h-11 rounded-xl bg-orange-400/15 border border-orange-400/50 flex items-center justify-center shadow-[0_0_22px_rgba(249,115,22,0.4)] group-hover:shadow-[0_0_30px_rgba(249,115,22,0.6)] transition-shadow">
            <ShieldCheck className="w-6 h-6 text-orange-300" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading font-extrabold text-2xl tracking-tight on-img">
              OTO<span className="text-orange-400 drop-shadow-[0_0_10px_rgba(249,115,22,0.7)]">KÜNYE</span>
            </span>
            <span className="mt-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-orange-300/80">Araç Geçmiş Raporu Platformu</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {links}
          {user ? (
            <div className="flex items-center gap-4">
              <NotificationBell />
              <span data-testid="nav-user-name" className="text-sm nav-ink">{user.name || user.email}</span>
              <button data-testid="nav-logout-button" onClick={handleLogout}
                className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg border border-white/10 nav-ink-danger hover:border-red-400/50 transition-colors">
                <LogOut className="w-4 h-4" /> Çıkış
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Link data-testid="nav-login-button" to="/giris"
                className="text-sm px-4 py-2 rounded-lg nav-ink transition-colors">
                Giriş Yap
              </Link>
              <Link data-testid="nav-register-button" to="/kayit"
                className="text-sm px-5 py-2 rounded-lg bg-orange-500 text-slate-950 font-semibold hover:bg-orange-400 hover:shadow-[0_0_20px_rgba(249,115,22,0.45)] transition-all">
                Kayıt Ol
              </Link>
              <span className="h-5 w-px bg-white/15" />
              <Link data-testid="nav-admin-login-button" to="/admin-giris"
                className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-orange-400/30 text-orange-300 hover:bg-orange-400/10 transition-colors">
                <ShieldCheck className="w-4 h-4" /> Admin Girişi
              </Link>
            </div>
          )}
        </nav>

        <button data-testid="nav-mobile-menu-button" className="md:hidden nav-ink" onClick={() => setOpen(!open)}>
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-orange-400/20 bg-[#0B1B33]/95 px-4 py-4 flex flex-col gap-4">
          {links}
          {user ? (
            <button data-testid="nav-mobile-logout-button" onClick={handleLogout}
              className="flex items-center gap-2 text-sm nav-ink-danger">
              <LogOut className="w-4 h-4" /> Çıkış Yap ({user.name || user.email})
            </button>
          ) : (
            <div className="flex gap-3">
              <Link data-testid="nav-mobile-login-button" to="/giris" onClick={() => setOpen(false)}
                className="flex-1 text-center text-sm px-4 py-2.5 rounded-lg border border-white/15 nav-ink">
                Giriş Yap
              </Link>
              <Link data-testid="nav-mobile-register-button" to="/kayit" onClick={() => setOpen(false)}
                className="flex-1 text-center text-sm px-4 py-2.5 rounded-lg bg-orange-500 text-slate-950 font-semibold">
                Kayıt Ol
              </Link>
            </div>
          )}
          {!user && (
            <Link data-testid="nav-mobile-admin-login" to="/admin-giris" onClick={() => setOpen(false)}
              className="flex items-center justify-center gap-1.5 text-xs text-orange-300/80 hover:text-orange-200 border-t border-white/10 pt-3">
              <ShieldCheck className="w-3.5 h-3.5" /> Admin Girişi
            </Link>
          )}
        </div>
      )}
    </header>
    {showDelete && (
      <div data-testid="delete-account-modal" className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-950/80" onClick={() => !deleting && setShowDelete(false)}>
        <div className="w-full max-w-md rounded-2xl border border-red-500/30 bg-slate-900 p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
          <div className="w-12 h-12 rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center mb-4">
            <Trash2 className="w-6 h-6 text-red-400" />
          </div>
          <h2 className="text-lg font-bold text-slate-100">Hesabınızı silmek üzeresiniz</h2>
          <p className="mt-3 text-sm text-slate-400 leading-relaxed">
            Kişisel hesap bilgileriniz kalıcı olarak silinecektir. Araç tarihçesinin bütünlüğünü korumak amacıyla araçlara ait geçmiş kayıtları hesabınızdan bağımsız olarak sistemde tutulacaktır. Bu işlem geri alınamaz.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button data-testid="delete-account-confirm" onClick={handleDeleteAccount} disabled={deleting}
              className="flex-1 py-3 rounded-xl bg-red-500 text-white text-sm font-bold hover:bg-red-400 transition-colors disabled:opacity-50">
              {deleting ? "Siliniyor..." : "Hesabımı Kalıcı Olarak Sil"}
            </button>
            <button data-testid="delete-account-cancel" onClick={() => setShowDelete(false)} disabled={deleting}
              className="px-5 py-3 rounded-xl border border-white/10 text-slate-300 text-sm font-semibold hover:border-white/25 transition-colors disabled:opacity-50">
              Vazgeç
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
